import { Controller } from "@hotwired/stimulus"

// share-modal controller
// Targets owned by this file (B13):
//   inviteForm, emailInput, roleSelect, inviteFeedback
// Targets owned by B14 (_members.html.erb):
//   memberList, pendingList
// Targets owned by B15 (_public_link.html.erb):
//   publicToggle, publicUrl
//
// To open: element.dispatchEvent(new CustomEvent("share-modal:open")) or call controller.open()
// B12 share button: data-action="click->share-modal#open"

export default class extends Controller {
  static targets = [
    "inviteForm",
    "emailInput",
    "roleSelect",
    "inviteFeedback",
    "memberList",
    "pendingList",
    "publicToggle",
    "publicUrlWrap",
    "publicUrl",
    "copyButton",
    "copyDefault",
    "copySuccess",
    "templatePublicationRow",
    "templatePublicationToggle"
  ]
  static values = {
    projectId: Number,
    endpointBasePath: { type: String, default: "/ui-planning" },
    baseUrl: String,
    inviteSuccess: String,
    inviteError: String,
    confirmRemoveMember: String,
    confirmRevokeInvitation: String,
    roleEditor: String,
    roleCommenter: String,
    roleViewer: String,
    roleOwner: String,
    memberEmpty: String,
    pendingBadge: String,
    btnRemove: String,
    btnCancel: String
  }

  // --- inline confirm state ---
  #activeConfirm = null   // { strip: HTMLElement, cleanup: Function }

  connect() {
    // 이 컨트롤러는 2곳에 붙는다: (1) 헤더 공유 버튼 (2) 모달 자체.
    // 초기 loadState 는 모달 인스턴스에서만 수행 (버튼은 fetch 불필요).
    if (this.#isModal()) {
      this.loadState()
      this.element.addEventListener("keydown", this.#handleTrapKeydown)
    }
    this.#boundKeydown = this.#handleKeydown.bind(this)
    this.#boundPointerdown = this.#handlePointerdown.bind(this)
  }

  disconnect() {
    if (this.#isModal()) {
      this.element.removeEventListener("keydown", this.#handleTrapKeydown)
    }
  }

  // --- open / close ---

  open(ev) {
    ev?.preventDefault()
    const modal = this.#modalElement()
    if (!modal) return
    modal.hidden = false
    const modalCtrl = this.application.getControllerForElementAndIdentifier(modal, "share-modal")
    if (modalCtrl) {
      modalCtrl.openerElement = ev?.currentTarget || document.activeElement
      modalCtrl.loadState()
      modalCtrl.focusFirstElement()
    }
  }

  close(ev) {
    ev?.preventDefault()
    const modal = this.#modalElement()
    if (!modal) return
    modal.hidden = true
    const modalCtrl = this.application.getControllerForElementAndIdentifier(modal, "share-modal")
    modalCtrl?.openerElement?.focus?.()
    if (modalCtrl) modalCtrl.openerElement = null
  }

  // --- focus trap (모달 인스턴스 전용) ---

  openerElement = null

  focusFirstElement() {
    this.#focusableElements()[0]?.focus()
  }

  #handleTrapKeydown = (ev) => {
    if (ev.key !== "Tab" || this.element.hidden) return
    const focusables = this.#focusableElements()
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault()
      last.focus()
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault()
      first.focus()
    }
  }

  #focusableElements() {
    return Array.from(this.element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )).filter(el => !el.disabled && !el.hidden && el.offsetParent !== null)
  }

  #isModal() {
    return this.element.classList.contains("project-share__modal")
  }

  #modalElement() {
    return this.#isModal() ? this.element : document.querySelector(".project-share__modal")
  }

  // --- state ---

  async loadState() {
    const res = await fetch(
      `${this.endpointBasePathValue}/projects/${this.projectIdValue}/share`,
      { headers: { Accept: "application/json" } }
    )
    if (!res.ok) return
    const data = await res.json()
    this.renderMembers(data)
    this.renderPublic(data.public_link)
  }

  // --- invite ---

  async submitInvite(ev) {
    ev.preventDefault()
    const email = this.emailInputTarget.value.trim()
    const role  = this.roleSelectTarget.value

    const res = await fetch(
      `${this.endpointBasePathValue}/projects/${this.projectIdValue}/share/invitations`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        },
        body: JSON.stringify({ email, role })
      }
    )

    if (res.ok) {
      this.inviteFeedbackTarget.textContent = this.inviteSuccessValue || "초대를 보냈어요"
      this.inviteFeedbackTarget.style.color = "#059669"
      this.emailInputTarget.value = ""
      this.loadState()
    } else {
      const err = await res.json().catch(() => ({}))
      this.inviteFeedbackTarget.textContent = err.error || this.inviteErrorValue || "초대에 실패했어요"
      this.inviteFeedbackTarget.style.color = "#DC2626"
    }
  }

  // --- members (B14 hook) ---
  // B14's _members.html.erb renders a container with:
  //   data-share-modal-target="memberList"   — accepted members list
  //   data-share-modal-target="pendingList"  — pending invitations list
  // Each member row uses:
  //   data-action="change->share-modal#changeMemberRole"  (on role <select>)
  //   data-action="click->share-modal#removeMember"       (on remove button)
  //   data-member-id="<id>"
  // Each pending row uses:
  //   data-action="click->share-modal#revokeInvitation"
  //   data-invitation-id="<id>"

  renderMembers(data) {
    if (!this.hasMemberListTarget) return

    const roleLabel = {
      owner: this.roleOwnerValue || "소유자",
      editor: this.roleEditorValue || "편집자",
      commenter: this.roleCommenterValue || "댓글 가능",
      viewer: this.roleViewerValue || "보기만"
    }

    const owner = data.owner ? `
      <li class="project-share__member-card">
        <div class="project-share__member-info">
          <span class="project-share__member-email">${this.#escapeHtml(data.owner.email)}</span>
          <span class="project-share__member-role-badge project-share__member-role-badge--owner">${roleLabel.owner}</span>
        </div>
      </li>
    ` : ""

    const members = (data.members || [])
      .map(m => `
        <li class="project-share__member-card" data-member-id="${m.id}">
          <div class="project-share__member-info">
            <span class="project-share__member-email">${this.#escapeHtml(m.email)}</span>
          </div>
          <div class="project-share__member-actions">
            <select class="project-share__member-role-select"
                    data-action="change->share-modal#changeMemberRole"
                    data-member-id="${m.id}">
              ${["editor","commenter","viewer"].map(r =>
                `<option value="${r}"${m.role === r ? " selected" : ""}>${roleLabel[r]}</option>`
              ).join("")}
            </select>
            <button type="button"
                    class="project-share__member-remove"
                    data-action="click->share-modal#removeMember"
                    data-member-id="${m.id}"
                    aria-label="멤버 제거">${this.btnRemoveValue || "제거"}</button>
          </div>
        </li>
      `)
      .join("")
    this.memberListTarget.innerHTML = owner + members || `<li class='project-share__member-empty'>${this.memberEmptyValue || "현재 멤버가 없어요"}</li>`

    if (!this.hasPendingListTarget) return

    const pending = (data.pending_invitations || data.invitations || [])
      .map(inv => `
        <li class="project-share__pending-card" data-invitation-id="${inv.id}">
          <div class="project-share__member-info">
            <span class="project-share__member-email">${this.#escapeHtml(inv.email)}</span>
            <span class="project-share__member-role-badge">${this.pendingBadgeValue || "수락 대기"} · ${roleLabel[inv.role] || inv.role}</span>
          </div>
          <button type="button"
                  class="project-share__member-remove"
                  data-action="click->share-modal#revokeInvitation"
                  data-invitation-id="${inv.id}"
                  aria-label="초대 취소">${this.btnCancelValue || "취소"}</button>
        </li>
      `)
      .join("")
    this.pendingListTarget.innerHTML = pending
  }

  async changeMemberRole(ev) {
    const memberId = ev.target.dataset.memberId
    const role     = ev.target.value
    await fetch(
      `${this.endpointBasePathValue}/projects/${this.projectIdValue}/share/members/${memberId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        },
        body: JSON.stringify({ role })
      }
    )
  }

  removeMember(ev) {
    const btn = ev.currentTarget
    const memberId = btn.dataset.memberId
    const message = this.confirmRemoveMemberValue || "이 멤버를 제거할까요?"
    this.#showInlineConfirm(btn, message, () => this.#doRemoveMember(memberId))
  }

  async #doRemoveMember(memberId) {
    const res = await fetch(
      `${this.endpointBasePathValue}/projects/${this.projectIdValue}/share/members/${memberId}`,
      {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        }
      }
    )
    if (res.ok) this.loadState()
  }

  revokeInvitation(ev) {
    const btn = ev.currentTarget
    const invId = btn.dataset.invitationId
    const message = this.confirmRevokeInvitationValue || "이 초대를 취소할까요?"
    this.#showInlineConfirm(btn, message, () => this.#doRevokeInvitation(invId))
  }

  async #doRevokeInvitation(invId) {
    const res = await fetch(
      `${this.endpointBasePathValue}/projects/${this.projectIdValue}/share/invitations/${invId}`,
      {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        }
      }
    )
    if (res.ok) this.loadState()
  }

  // --- inline confirm helpers ---

  #boundKeydown = null
  #boundPointerdown = null

  #showInlineConfirm(triggerBtn, message, onConfirm) {
    // Remove any existing confirm strip first (single-instance rule)
    this.#dismissInlineConfirm()

    // Determine the row element (closest li or the button's parentElement)
    const row = triggerBtn.closest("li") || triggerBtn.parentElement

    // Determine the confirm button label from the trigger button's own text
    const confirmLabel = triggerBtn.textContent.trim() || "확인"

    // Build the strip element
    const strip = document.createElement("div")
    strip.className = "share-inline-confirm"
    Object.assign(strip.style, {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 12px",
      background: "#FEF2F2",
      borderTop: "1px solid #FECACA",
      flexWrap: "wrap"
    })

    const msg = document.createElement("span")
    msg.className = "share-inline-confirm__message"
    msg.textContent = message
    Object.assign(msg.style, {
      flex: "1",
      fontSize: "13px",
      color: "#374151"
    })

    const confirmBtn = document.createElement("button")
    confirmBtn.type = "button"
    confirmBtn.className = "share-inline-confirm__confirm"
    confirmBtn.textContent = confirmLabel
    Object.assign(confirmBtn.style, {
      padding: "4px 12px",
      background: "#DC2626",
      color: "#FFFFFF",
      border: "none",
      borderRadius: "0",
      fontSize: "13px",
      cursor: "pointer",
      fontFamily: "inherit"
    })

    const cancelBtn = document.createElement("button")
    cancelBtn.type = "button"
    cancelBtn.className = "share-inline-confirm__cancel"
    cancelBtn.textContent = "취소"
    Object.assign(cancelBtn.style, {
      padding: "4px 12px",
      background: "transparent",
      color: "#374151",
      border: "1px solid #E5E7EB",
      borderRadius: "0",
      fontSize: "13px",
      cursor: "pointer",
      fontFamily: "inherit"
    })

    strip.append(msg, confirmBtn, cancelBtn)
    row.append(strip)

    // Wire up confirm button
    const handleConfirm = () => {
      this.#dismissInlineConfirm()
      onConfirm()
    }
    confirmBtn.addEventListener("click", handleConfirm)
    cancelBtn.addEventListener("click", () => this.#dismissInlineConfirm())

    // Register global listeners for Escape and outside-click
    document.addEventListener("keydown", this.#boundKeydown)
    document.addEventListener("pointerdown", this.#boundPointerdown, true)

    this.#activeConfirm = { strip, row }
  }

  #dismissInlineConfirm() {
    if (!this.#activeConfirm) return
    this.#activeConfirm.strip.remove()
    this.#activeConfirm = null
    document.removeEventListener("keydown", this.#boundKeydown)
    document.removeEventListener("pointerdown", this.#boundPointerdown, true)
  }

  #handleKeydown(ev) {
    if (ev.key === "Escape") this.#dismissInlineConfirm()
  }

  #handlePointerdown(ev) {
    if (!this.#activeConfirm) return
    const strip = this.#activeConfirm.strip
    if (!strip.contains(ev.target)) this.#dismissInlineConfirm()
  }

  // --- public link (B15 hook) ---
  // B15's _public_link.html.erb renders:
  //   data-share-modal-target="publicToggle"  — <input type="checkbox">
  //   data-share-modal-target="publicUrl"     — <input type="text"> or <span>
  // Toggle uses:
  //   data-action="change->share-modal#togglePublic"
  // Copy button uses:
  //   data-action="click->share-modal#copyPublicUrl"

  renderPublic(publicLink) {
    if (!this.hasPublicToggleTarget) return

    const enabled = publicLink?.enabled ?? false
    this.publicToggleTarget.checked = enabled

    if (this.hasPublicUrlWrapTarget) {
      this.publicUrlWrapTarget.hidden = !enabled
    }

    if (this.hasPublicUrlTarget) {
      this.publicUrlTarget.value = enabled ? (publicLink?.url || "") : ""
      this.publicUrlTarget.disabled = !enabled
    }

    this.#renderTemplatePublication(publicLink, enabled)
  }

  async togglePublic(ev) {
    const enabled = ev.target.checked
    const res = await fetch(
      `${this.endpointBasePathValue}/projects/${this.projectIdValue}/share/public_link`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        },
        body: JSON.stringify({ enabled })
      }
    )
    if (res.ok) {
      const data = await res.json()
      this.renderPublic(data.public_link)
    } else {
      ev.target.checked = !enabled // revert on failure
    }
  }

  async toggleTemplatePublication(ev) {
    const enabled = ev.target.checked
    const res = await fetch(
      `${this.endpointBasePathValue}/projects/${this.projectIdValue}/share/template_publication`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          Accept: "application/json"
        },
        body: JSON.stringify({ enabled })
      }
    )
    if (res.ok) {
      const data = await res.json()
      this.renderPublic(data.public_link)
    } else {
      ev.target.checked = !enabled
    }
  }

  async copyPublicUrl(ev) {
    ev?.preventDefault()
    if (!this.hasPublicUrlTarget) return
    const url = this.publicUrlTarget.value
    if (!url) return

    let copied = false
    try {
      await navigator.clipboard.writeText(url)
      copied = true
    } catch {
      // fallback: select the input for manual copy
      this.publicUrlTarget.select()
      try { document.execCommand("copy"); copied = true } catch {}
    }

    if (copied) this.#flashCopied()
  }

  #flashCopied() {
    if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.add("is-copied")
    if (this.hasCopyDefaultTarget) this.copyDefaultTarget.hidden = true
    if (this.hasCopySuccessTarget) this.copySuccessTarget.hidden = false

    clearTimeout(this.#copyTimer)
    this.#copyTimer = setTimeout(() => {
      if (this.hasCopyButtonTarget) this.copyButtonTarget.classList.remove("is-copied")
      if (this.hasCopyDefaultTarget) this.copyDefaultTarget.hidden = false
      if (this.hasCopySuccessTarget) this.copySuccessTarget.hidden = true
    }, 2000)
  }

  #copyTimer = null

  #renderTemplatePublication(publicLink, publicLinkEnabled) {
    if (!this.hasTemplatePublicationToggleTarget) return

    const published = publicLink?.published_to_templates ?? false
    this.templatePublicationToggleTarget.checked = publicLinkEnabled && published
    this.templatePublicationToggleTarget.disabled = !publicLinkEnabled

    if (this.hasTemplatePublicationRowTarget) {
      this.templatePublicationRowTarget.classList.toggle("is-disabled", !publicLinkEnabled)
    }
  }

  // --- utils ---

  #escapeHtml(str) {
    return String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }
}
